/**
 * Phase 22 — Dashboard budget cards.
 *
 * Renders Today's spend + This month's spend as KPI tiles with usage
 * bars against the configured caps. Status pill color follows the
 * verdict returned by ``GET /api/v1/safety/status``.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Wallet, TrendingUp } from "lucide-react";

function pillClass(status) {
  if (status === "blocked")
    return "bg-rose-100 text-rose-800 border-rose-300";
  if (status === "warn")
    return "bg-amber-100 text-amber-800 border-amber-300";
  if (status === "ok") return "bg-emerald-100 text-emerald-800 border-emerald-300";
  return "bg-slate-100 text-slate-600 border-slate-300";
}
function pillLabel(status) {
  if (status === "blocked") return "Blocked";
  if (status === "warn") return "≥80% used";
  if (status === "ok") return "Healthy";
  return "No cap";
}

function BudgetCard({ label, icon, spend, window }) {
  const pct = window?.pct;
  const cap = window?.cap_usd;
  return (
    <Card data-testid={`budget-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          {icon}
          {label}
        </CardTitle>
        <Badge variant="outline"
                  className={`text-[10px] font-mono ${pillClass(window?.status)}`}
                  data-testid={`budget-status-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {pillLabel(window?.status)}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums">
          ${spend?.total_usd?.toFixed(2) ?? "0.00"}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {cap ? (
            <>of ${cap.toFixed(2)} cap · {pct?.toFixed(1)}%</>
          ) : (
            <>No cap configured</>
          )}
        </div>
        {cap ? (
          <Progress
            className="mt-3 h-1.5"
            value={Math.min(100, pct || 0)}
          />
        ) : null}
        <div className="text-[10px] text-muted-foreground mt-2 font-mono">
          LLM ${spend?.llm_cost_usd?.toFixed(2) ?? "0.00"} · Ads ${spend?.ad_spend_usd?.toFixed(2) ?? "0.00"}
        </div>
      </CardContent>
    </Card>
  );
}

export default function BudgetStatusCards() {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const r = await api.get("/safety/status",
          { skipPermissionToast: true });
        if (mounted) setStatus(r.data);
      } catch (e) {
        if (mounted) setErr(true);
      }
    }
    load();
    const id = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (err || !status) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4"
             data-testid="dashboard-budget-cards">
      <BudgetCard
        label="Today's spend"
        icon={<Wallet className="size-3" />}
        spend={status.today}
        window={status.verdict?.daily}
      />
      <BudgetCard
        label="This month"
        icon={<TrendingUp className="size-3" />}
        spend={status.this_month}
        window={status.verdict?.monthly}
      />
    </div>
  );
}
